import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import './MultiSelect.css';
import './ConferencesMultiSelect.css';

const MultiSelect = ({ 
  options, 
  selectedValues, 
  onChange, 
  placeholder = 'Sélectionner...', 
  label, 
  dropdownClassName 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const updateDropdownPosition = useCallback(() => {
    const node = dropdownRef.current;
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
    const margin = 12;

    let width = rect.width;
    let left = rect.left;
    if (viewportWidth) {
      if (left + width + margin > viewportWidth) {
        left = Math.max(margin, viewportWidth - width - margin);
      }
      if (left < margin) {
        left = margin;
      }
    }

    let top = rect.bottom + 6;
    let maxHeight = viewportHeight - top - margin;
    if (!Number.isFinite(maxHeight)) {
      maxHeight = 360;
    }
    maxHeight = Math.min(480, maxHeight);

    if (maxHeight < 220) {
      const fallback = Math.min(480, Math.max(0, viewportHeight - margin * 2));
      maxHeight = Math.max(180, fallback);
      top = Math.max(margin, Math.min(top, viewportHeight - maxHeight - margin));
    }

    setDropdownPosition({
      top: Math.round(top),
      left: Math.round(left),
      width: Math.round(width),
      maxHeight: Math.round(maxHeight),
    });
  }, []);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus sur l'input de recherche quand on ouvre le dropdown
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updateDropdownPosition();

    const handleResize = () => updateDropdownPosition();
    const handleScroll = () => updateDropdownPosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, selectedValues, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  // Filtrer les options selon le terme de recherche
  const filteredOptions = options.filter(option => 
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gérer la sélection/désélection d'une option
  const handleOptionClick = (option) => {
    let newSelectedValues;
    if (selectedValues.includes(option)) {
      newSelectedValues = selectedValues.filter(value => value !== option);
    } else {
      newSelectedValues = [...selectedValues, option];
    }
    onChange(newSelectedValues);
    // Ne pas fermer le dropdown après sélection
    // setIsOpen(false); // supprimé
  };

  // Supprimer une valeur sélectionnée
  const handleRemoveValue = (value, e) => {
    e.stopPropagation();
    const newSelectedValues = selectedValues.filter(v => v !== value);
    console.log(`MultiSelect - ${label} - Suppression de ${value}:`, newSelectedValues);
    onChange(newSelectedValues);
  };

  // Effacer toutes les valeurs sélectionnées
  const handleClearAll = (e) => {
    e.stopPropagation();
    console.log(`MultiSelect - ${label} - Effacement de toutes les valeurs`);
    onChange([]);
  };

  // Log pour le débogage
  useEffect(() => {
    console.log(`MultiSelect - ${label} - Options disponibles:`, options);
    console.log(`MultiSelect - ${label} - Valeurs sélectionnées:`, selectedValues);
  }, [options, selectedValues, label]);

  const dropdownStyle = dropdownPosition
    ? {
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        maxHeight: `${dropdownPosition.maxHeight}px`,
        visibility: 'visible',
      }
    : { visibility: 'hidden' };

  return (
    <div className="multi-select-container" ref={dropdownRef}>
      <label className="multi-select-label">{label}</label>
      
      <div 
        className={`multi-select-input ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="selected-values">
          {selectedValues.length === 0 ? (
            <span className="placeholder">{placeholder}</span>
          ) : (
            <div className="selected-tags">
              {selectedValues.map(value => (
                <div key={value} className="selected-tag">
                  <span>{value}</span>
                  <button 
                    className="remove-tag-btn"
                    onClick={(e) => handleRemoveValue(value, e)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="multi-select-actions">
          {selectedValues.length > 0 && (
            <button 
              className="clear-all-btn"
              onClick={handleClearAll}
            >
              ×
            </button>
          )}
          <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
      </div>
      
      {isOpen && (
        <div
          className={dropdownClassName ? dropdownClassName : 'dropdown-menu'}
          style={dropdownStyle}
        >
          <div className="search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="options-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div 
                  key={option} 
                  className={`option ${selectedValues.includes(option) ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionClick(option);
                  }}
                >
                  <span className="checkbox">
                    {selectedValues.includes(option) && '✓'}
                  </span>
                  <span className="option-label">{option}</span>
                </div>
              ))
            ) : (
              <div className="no-options">Aucun résultat</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
